import { ApiRequestError, type ApiEnvelope } from '@/api/errors';
import type {
  AuthTokens,
  LoginRequest,
  RegisterRequest,
  ValidateInnData,
  ValidateInnRequest,
} from '@/types/auth';
import type { EmployeeInvitation, EmployeeInvitationInfo } from '@/types/employee';
import { BaseService } from './BaseService';

const FORM_HEADERS = { 'Content-Type': 'application/x-www-form-urlencoded' } as const;
// Публичные auth-эндпоинты: без авторизации (auth:false) → их 401/4xx не запускают refresh.
const PUBLIC = { auth: false } as const;

interface TokenData {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
}

function toTokens(envelope: ApiEnvelope<TokenData>): AuthTokens {
  if (envelope?.success === false) {
    throw new ApiRequestError(envelope.error_description ?? envelope.error ?? 'Auth failed', {
      code: envelope.error,
      description: envelope.error_description,
    });
  }
  const data = envelope?.data;
  if (!data?.access_token || !data.refresh_token) {
    throw new ApiRequestError('Auth response is missing tokens');
  }
  return { accessToken: data.access_token, refreshToken: data.refresh_token, ttl: data.expires_in };
}

// Сервис user-management: auth/регистрация/восстановление. DTO — из src/types/auth + сгенерённых
// типов. Эндпоинты на @RequestParam шлём form-urlencoded телом (секреты не в URL).
class UserManagementService extends BaseService {
  async login(credentials: LoginRequest): Promise<AuthTokens> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      {
        method: 'post',
        url: '/usermanagement/registration/login',
        data: new URLSearchParams({
          userName: credentials.userName,
          password: credentials.password,
        }),
        headers: FORM_HEADERS,
      },
      { ...PUBLIC, retry: false },
    );
    // 204 = пользователь не найден (пустое тело). Пробрасываем статус для экрана входа.
    if (response.status === 204 || !response.data?.data?.access_token) {
      throw new ApiRequestError('Login failed', {
        status: response.status,
        code: response.data?.error,
        description: response.data?.error_description,
      });
    }
    return toTokens(response.data);
  }

  async register(data: RegisterRequest): Promise<string | undefined> {
    const response = await this.request<ApiEnvelope<{ emailValidationId?: string }>>(
      { method: 'post', url: '/usermanagement/registration', data },
      { ...PUBLIC, retry: false },
    );
    const envelope = response.data;
    if (envelope?.success === false || envelope?.error) {
      throw new ApiRequestError(envelope.error_description ?? envelope.error ?? 'Registration failed', {
        status: response.status,
        code: envelope.error,
        description: envelope.error_description,
      });
    }
    return envelope?.data?.emailValidationId;
  }

  /** Автозаполнение по ИНН — read-only lookup, идемпотентно → повтор безопасен. */
  async validateInn(request: ValidateInnRequest): Promise<ValidateInnData> {
    const response = await this.request<ApiEnvelope<ValidateInnData>>(
      {
        method: 'post',
        url: '/usermanagement/registration/validate-inn',
        data: { type: request.type, inn: request.inn, checkUnique: false },
      },
      { ...PUBLIC, idempotent: true },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Запрос письма для сброса пароля. Отправка письма не идемпотентна → без повтора. */
  async requestPasswordReset(email: string): Promise<void> {
    await this.request(
      {
        method: 'post',
        url: '/usermanagement/registration/reset-password',
        data: new URLSearchParams({ email }),
        headers: FORM_HEADERS,
      },
      { ...PUBLIC, retry: false },
    );
  }

  /** Установка нового пароля по requestId из письма. 406 = ссылка устарела. */
  async changePassword(requestId: string, newPassword: string): Promise<void> {
    await this.request(
      {
        method: 'post',
        url: '/usermanagement/registration/change-password',
        data: new URLSearchParams({ requestId, newPassword }),
        headers: FORM_HEADERS,
      },
      { ...PUBLIC, retry: false },
    );
  }

  /**
   * Self-service смена собственного пароля (текущий → новый) авторизованным пользователем.
   * Бэкенд проксирует в Identix Account API; Identix проверяет текущий пароль и отзывает старые токены,
   * а бэкенд бесшовно переполучает пару — возвращаем её для {@link useAuth().loginWithTokens}.
   * Если бэкенд не смог переполучить (data=null) — возвращаем null (вызывающий должен разлогинить).
   * Ошибки различаются по статусу выше: 422 — неверный текущий пароль, 400 — новый отклонён.
   */
  async changeOwnPassword(currentPassword: string, newPassword: string): Promise<AuthTokens | null> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      {
        method: 'post',
        url: '/usermanagement/self-management/change-password',
        data: { currentPassword, newPassword },
      },
      { retry: false },
    );
    return response.data?.data?.access_token ? toTokens(response.data) : null;
  }

  async verifyEmail(id: string): Promise<AuthTokens> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      {
        method: 'post',
        url: `/usermanagement/registration/verify-email/${encodeURIComponent(id)}`,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      },
      { ...PUBLIC, retry: false },
    );
    return toTokens(response.data);
  }

  async verifyInvitationEmail(id: string): Promise<AuthTokens> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      { method: 'post', url: `/usermanagement/invitation/verify-email/${encodeURIComponent(id)}` },
      { ...PUBLIC, retry: false },
    );
    return toTokens(response.data);
  }

  /**
   * Обмен одноразового impersonation-кода (из redirect Identix на /impersonation/callback) на сессию
   * целевого пользователя. Публичный (auth:false), без повтора — код одноразовый (60с).
   */
  async exchangeImpersonationCode(code: string): Promise<AuthTokens> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      { method: 'post', url: '/usermanagement/impersonation/exchange', data: { code } },
      { ...PUBLIC, retry: false },
    );
    return toTokens(response.data);
  }

  /**
   * Приглашение сотрудника: данные для страницы приёма. Публичный — у приглашённого ещё нет
   * учётной записи, предъявить токен он не может.
   */
  async validateEmployeeInvitation(id: string): Promise<EmployeeInvitationInfo> {
    const response = await this.request<ApiEnvelope<EmployeeInvitationInfo>>(
      { method: 'get', url: `/usermanagement/employee-invitation/validate/${encodeURIComponent(id)}` },
      { ...PUBLIC, idempotent: true },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Приём приглашения: сотрудник задаёт пароль и сразу получает сессию. */
  async acceptEmployeeInvitation(id: string, password: string): Promise<AuthTokens> {
    const response = await this.request<ApiEnvelope<TokenData>>(
      {
        method: 'post',
        url: `/usermanagement/employee-invitation/accept/${encodeURIComponent(id)}`,
        data: { password },
      },
      { ...PUBLIC, retry: false },
    );
    return toTokens(response.data);
  }

  /** Приглашения организации для экрана «Сотрудники». */
  async getEmployeeInvitations(): Promise<EmployeeInvitation[]> {
    const response = await this.request<ApiEnvelope<EmployeeInvitation[]>>(
      { method: 'get', url: '/usermanagement/employee-invitation/list' },
      { idempotent: true },
    );
    return this.unwrap(response.data, response.status) ?? [];
  }

  /** Отправка приглашения. Письмо не идемпотентно → без повтора. */
  async sendEmployeeInvitation(email: string, displayName?: string): Promise<EmployeeInvitation> {
    const response = await this.request<ApiEnvelope<EmployeeInvitation>>(
      { method: 'post', url: '/usermanagement/employee-invitation/send', data: { email, displayName } },
      { retry: false },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Повторная отправка приглашения с продлением срока. */
  async resendEmployeeInvitation(id: string): Promise<EmployeeInvitation> {
    const response = await this.request<ApiEnvelope<EmployeeInvitation>>(
      { method: 'post', url: `/usermanagement/employee-invitation/resend/${encodeURIComponent(id)}` },
      { retry: false },
    );
    return this.unwrap(response.data, response.status);
  }

  /**
   * Исключить сотрудника из организации: закрывает ему доступ и снимает членство.
   * Необратимо — вызывается после подтверждения. Требует роли MANAGER (владелец организации).
   */
  async removeOrganizationMember(userId: number): Promise<void> {
    await this.request<ApiEnvelope<void>>(
      { method: 'delete', url: `/usermanagement/organization/member/${userId}` },
      { retry: false },
    );
  }

  /**
   * Распустить организацию: закрыть доступ всем сотрудникам, включая владельца. Необратимо.
   * Бэкенд отвергает (406), если у организации есть незавершённые сделки или экспедирование.
   * Требует роли MANAGER.
   */
  async dissolveOrganization(): Promise<void> {
    await this.request<ApiEnvelope<void>>(
      { method: 'delete', url: '/usermanagement/organization' },
      { retry: false },
    );
  }

  /** Повторная отправка письма подтверждения. Отправка письма не идемпотентна → без повтора. */
  async resendRegistrationEmail(email: string): Promise<void> {
    await this.request(
      {
        method: 'post',
        url: `/usermanagement/registration/verify-email/resend/${encodeURIComponent(email)}`,
      },
      { ...PUBLIC, retry: false },
    );
  }
}

export const userManagementService = new UserManagementService();
